/* 0.49.20 - Values from the page's address: /hy/services/apply?training=T-12
 * makes url.training read "T-12" in formulas, texts and rule conditions.
 *
 * Safe by shape: a name is a letter then letters, digits or _ (up to 41),
 * and a value is letters of any alphabet, digits, "-", "_" and "." (up to
 * 100), so no sentence, link, space or markup can come through. A value of
 * any other shape reads as empty. A value is never run as a formula.
 * Address values are what the visitor's link says: the receiving API still
 * checks them, and no price or right is ever taken from one. */
export const URL_NAME_RE = /^[A-Za-z][A-Za-z0-9_]{0,40}$/;
export const URL_VALUE_RE = /^[\p{L}\p{N}._-]{1,100}$/u;

/* 0.49.21 - `declared` (the Address values setting): a name with
   Allowed values takes only one of them; any other value reads as empty,
   as a value of the wrong shape does. The page's rules then decide what
   each code means (url.input equals "abc": set this, call that). */
export function readUrlValues(search, declared = null) {
  const values = {};
  const invalid = new Set();
  const allow = {};
  for (const d of (Array.isArray(declared) ? declared : [])) {
    const list = d && Array.isArray(d.allowed) ? d.allowed.map(String).filter(Boolean) : [];
    if (d && d.name && list.length) allow[d.name] = new Set(list);
  }
  let q;
  try { q = new URLSearchParams(search || ''); } catch (_e) { return { values, invalid }; }
  for (const [rawName, rawValue] of q.entries()) {
    const name = String(rawName || '').trim();
    if (!URL_NAME_RE.test(name) || Object.prototype.hasOwnProperty.call(values, name)) continue;
    const v = String(rawValue ?? '').trim();
    if (URL_VALUE_RE.test(v) && (!allow[name] || allow[name].has(v))) values[name] = v;
    else { values[name] = ''; if (v) invalid.add(name); }
  }
  return { values, invalid };
}

/* The declared names that are required and have no usable value. */
export function missingRequired(declared, values) {
  return (Array.isArray(declared) ? declared : [])
    .filter(d => d && d.required && URL_NAME_RE.test(String(d.name || '')))
    .map(d => String(d.name))
    .filter(n => !values || !values[n]);
}

/* As saved: names that fit, once each. */
export function cleanUrlValues(list) {
  const out = [];
  const seen = new Set();
  for (const d of (Array.isArray(list) ? list : [])) {
    const name = String((d && d.name) || '').trim();
    if (!URL_NAME_RE.test(name) || seen.has(name)) continue;
    seen.add(name);
    const entry = { name };
    if (d && d.required) entry.required = true;
    // 0.49.21 - Allowed values: a list, or text split at commas.
    const raw = d && (Array.isArray(d.allowed) ? d.allowed : String(d.allowed || '').split(','));
    const allowed = [...new Set((raw || []).map(x => String(x).trim()).filter(x => URL_VALUE_RE.test(x)))];
    if (allowed.length) entry.allowed = allowed;
    out.push(entry);
  }
  return out;
}

export function badUrlValue(list) {
  return (Array.isArray(list) ? list : []).find(d => d && String(d.name || '').trim()
    && !URL_NAME_RE.test(String(d.name).trim())) || null;
}
